import type { CompanyResponse, LicenceResponse, ListResponse } from '@shared/schemas';

import type { EnrichmentStats } from '../../_shared/domain/stats.domain';
import type { EnrichmentCandidate, EnrichmentType } from '../domain/candidates.domain';
import type { BucketKey } from '../domain/cohort.domain';
import type { EnrichmentSession } from '../domain/sessions.domain';

/** Lifecycle of the create-session write (drives the submit button + page effects). */
export type CreateStatus = 'idle' | 'submitting' | 'succeeded' | 'failed';

/**
 * The New Session panel's state: what was asked for, plus the dropdown data the
 * saga fetches through the gateway. Every field is serialisable.
 *
 * The cohort is no longer a hand-picked list of entities held in a component:
 * it is the buckets that are ticked, resolved against the fetched pool by
 * `buildCohort`. That is what makes it reproducible; and what a session will
 * eventually be able to record about itself.
 */
export interface PanelState {
  open: boolean;
  // ── form fields ──
  type: EnrichmentType;
  licenceId: string | null;
  listId: string | null;
  currentOnly: boolean;
  /** Selected type-filter values; UNSET_TYPE_VALUE represents "no type set". */
  filterTypes: string[];
  /** Which buckets run. Not exclusive; `target_ids` always accepted any mix. */
  includedBuckets: BucketKey[];
  /**
   * Set when the panel was opened by re-asking a past run's question, so it can
   * say whose question it is and how much the answer has moved since. Null on a
   * panel opened from the button, which answers nothing in particular.
   */
  reaskedFrom: { previousCount: number; previousAt: string } | null;
  // ── fetched dropdown data ──
  licences: LicenceResponse[];
  profileTypes: string[];
  lists: ListResponse[];
  candidates: EnrichmentCandidate[];
  companies: CompanyResponse[];
  // ── per-resource loading flags (drive the dropdown spinners) ──
  licencesLoading: boolean;
  profileTypesLoading: boolean;
  listsLoading: boolean;
  entitiesLoading: boolean;
}

/**
 * Redux state for the admin enrichment-sessions page: the sessions table, the KPI
 * stats, the New Session panel, and the create-session write lifecycle.
 */
export interface SessionsState {
  rows: EnrichmentSession[];
  loading: boolean;
  error: string | null;
  /** KPI stats; null until first loaded (the KPI row renders skeletons). */
  stats: EnrichmentStats | null;
  panel: PanelState;
  createStatus: CreateStatus;
  createError: string | null;
  /** Id of the freshly created session (the page navigates to it). */
  createdSessionId: string | null;
}

export const initialPanelState: PanelState = {
  open: false,
  type: 'linkedin_standard',
  licenceId: null,
  listId: null,
  currentOnly: false,
  filterTypes: [],
  // The cohort a session is almost always for: the panel opens on a live button
  // rather than on a disabled one under a screen announcing what it could run.
  includedBuckets: ['new'],
  reaskedFrom: null,
  licences: [],
  profileTypes: [],
  lists: [],
  candidates: [],
  companies: [],
  licencesLoading: false,
  profileTypesLoading: false,
  listsLoading: false,
  entitiesLoading: false,
};

export const initialState: SessionsState = {
  rows: [],
  loading: false,
  error: null,
  stats: null,
  panel: initialPanelState,
  createStatus: 'idle',
  createError: null,
  createdSessionId: null,
};
